import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import { createContext, useCallback, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'
import { fetchMe, fetchRuntimeConfig, selectBiller } from '../bff/client'
import { ROLE_INTERNAL, type Me, type RuntimeConfig, type SessionUser } from '../bff/types'
import { useI18n } from '../i18n'
import { createBusinessTime, type BusinessTime } from '../time/businessTime'

export const meQueryKey = ['bff', 'me'] as const
export const configQueryKey = ['bff', 'config'] as const

export type SessionStatus = 'loading' | 'anonymous' | 'authenticated'

/** An explicit type guard rather than an inline `?.authenticated === true`:
 *  optional chaining does not narrow a discriminated union, so without this
 *  every read of `user`/`csrfToken` below is an error against the anonymous
 *  arm — and casting it away would defeat the point of modelling anonymous
 *  as its own shape. */
type AuthenticatedMe = Extract<Me, { authenticated: true }>
const isAuthenticated = (m: Me | undefined): m is AuthenticatedMe => m?.authenticated === true

interface SessionContextValue {
  readonly status: SessionStatus
  readonly user: SessionUser | null
  readonly csrfToken: string | null
  readonly config: RuntimeConfig | null
  readonly businessTime: BusinessTime | null
  /** Internal users only. Sets the biller as SERVER-SIDE session state and
   *  then invalidates everything scoped to it. */
  readonly switchBiller: (billerId: string) => Promise<void>
  readonly canManageUsers: boolean
}

const SessionContext = createContext<SessionContextValue | null>(null)

/** Anything scoped to the selected biller must be dropped when it changes,
 *  or the previous biller's rows stay on screen under the new biller's name —
 *  a tenant-isolation bug the user can see. */
async function invalidateBillerScoped(client: QueryClient): Promise<void> {
  await client.invalidateQueries({ queryKey: ['bff'] })
  await client.invalidateQueries({ queryKey: ['api'] })
}

export function SessionProvider({ children }: { readonly children: ReactNode }) {
  const client = useQueryClient()
  const { locale } = useI18n()

  // /bff/me answers 200 for anonymous, so this is not an error path — an
  // unauthenticated visitor is a normal, expected result.
  const me = useQuery<Me>({
    queryKey: meQueryKey,
    queryFn: ({ signal }) => fetchMe(signal),
    staleTime: 60_000,
    retry: false,
  })

  const config = useQuery<RuntimeConfig>({
    queryKey: configQueryKey,
    queryFn: ({ signal }) => fetchRuntimeConfig(signal),
    // Static per deployment; there is no reason to ask twice in a session.
    staleTime: Infinity,
    retry: 1,
  })

  const meData: Me | undefined = me.data
  const authenticated: AuthenticatedMe | null = isAuthenticated(meData) ? meData : null
  const csrfToken = authenticated?.csrfToken ?? null

  const switchBiller = useCallback(
    async (billerId: string) => {
      if (!csrfToken) throw new Error('cannot switch biller without a session')
      await selectBiller(billerId, csrfToken)
      await invalidateBillerScoped(client)
    },
    [csrfToken, client],
  )

  const businessTime = useMemo(
    () =>
      config.data
        ? createBusinessTime(config.data.businessTimezone, locale, config.data.currency)
        : null,
    [config.data, locale],
  )

  const value = useMemo<SessionContextValue>(() => {
    /*
     * 'authenticated' requires BOTH queries, not just /bff/me.
     *
     * useAuthenticatedSession promises a user, a csrfToken AND a businessTime,
     * and throws if any is missing. businessTime is derived from /bff/config,
     * which is a SEPARATE request, so reporting 'authenticated' the moment
     * /bff/me resolved opened a window where RequireSession let children
     * render and the first one to ask for businessTime threw
     * "used outside an authenticated route".
     *
     * Only visible on a COLD load, because a warm query cache answers config
     * instantly. Which is exactly what the Keycloak forgot-password redirect
     * is: a fresh page load with an empty cache, landing straight on a screen
     * that formats dates. Navigating anywhere afterwards worked, and that made
     * it look like a routing problem rather than a race.
     *
     * The invariant belongs here, where the promise is made, rather than in
     * RequireSession, or any future gate would have to remember it too.
     */
    const status: SessionStatus =
      me.isPending || config.isPending ? 'loading' : authenticated ? 'authenticated' : 'anonymous'
    const user = authenticated?.user ?? null
    return {
      status,
      user,
      csrfToken,
      config: config.data ?? null,
      businessTime,
      switchBiller,
      // Mirrors the API's rule rather than inventing a client-side one: the
      /*
       * INTERNAL ONLY. BRD BR-04: "Biller user accounts are created
       * exclusively by Maviance Operations. Billers may not create, modify,
       * or delete user accounts." A2 and the Out-of-Scope list say it twice
       * more. No biller-side role grants this, which is why the portal has
       * only one.
       *
       * Gates the NAV ONLY — the API enforces it again, and that is the
       * enforcement that counts.
       */
      canManageUsers: user?.roles.includes(ROLE_INTERNAL) === true,
    }
  }, [me.isPending, config.isPending, authenticated, csrfToken, config.data, businessTime, switchBiller])

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>')
  return ctx
}

/** Narrowed accessor for screens that only render behind an authenticated
 *  route, so they need not re-check for null on every field. */
export function useAuthenticatedSession(): SessionContextValue & {
  readonly user: SessionUser
  readonly csrfToken: string
  readonly businessTime: BusinessTime
} {
  const session = useSession()
  if (!session.user || !session.csrfToken || !session.businessTime) {
    throw new Error('useAuthenticatedSession used outside an authenticated route')
  }
  return session as SessionContextValue & {
    user: SessionUser
    csrfToken: string
    businessTime: BusinessTime
  }
}
